// src/features/tag-types/components/TagTypeEdit.tsx
import React, { useState } from 'react';
import { 
  Edit, 
  SimpleForm, 
  TextInput, 
  BooleanInput,
  SelectInput,
  required, 
  useNotify, 
  useRedirect, 
  useRecordContext,
  LoadingIndicator,
  useTranslate,
} from 'react-admin';
import { 
  Grid,
  Typography,
  Box,
} from '@mui/material';
import { Receipt as TagIcon } from '@mui/icons-material';
import { MyToolbar } from '../../../components/forms/MyToolbar';
import { MyFormContainer } from '../../../components/forms/MyFormContainer';
import { 
  CenteredFormLayout,
  MyFormSection 
} from '../../../components/forms/MyFormLayout';
import BaseDeleteDialog from '../../../components/common/BaseDeleteDialog';
import { TagType, VALID_TYPE_CODES } from '../types';
import { useTagTypeValidation } from '../hooks/useTagTypeValidation';

const TagTypeEditTitle: React.FC = () => {
  const record = useRecordContext<TagType>();
  const translate = useTranslate();
  
  if (!record) return <LoadingIndicator />;
  
  return (
    <span>
      {translate('resources.tag-types.titles.edit', 'Editar Tipo de ID')}: {record.name}
    </span>
  );
};

const TagTypeEdit: React.FC = (props) => {
  const notify = useNotify();
  const redirect = useRedirect();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Transform actualizado para incluir isSystemGenerated
  const transform = (data: any) => {
    return {
      typeCode: data.typeCode?.toUpperCase().replace(/\s/g, '_'),
      name: data.name?.trim(),
      description: data.description?.trim() || '',
      requiresManualInput: data.requiresManualInput || false,
      isSystemGenerated: data.isSystemGenerated || false, // 👈 AGREGADO
    };
  };

  const handleSuccess = (data: any) => {
    notify('resources.tag-types.messages.update_success', { 
      type: 'success',
      messageArgs: { name: data?.name }
    });
    redirect('list', 'tag-types');
  };

  const handleError = (error: any) => {
    console.error('Mutation error:', error);
    notify(
      error?.body?.message || 
      error?.message ||
      'resources.tag-types.messages.update_error', 
      { type: 'error' }
    );
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  return (
    <Edit 
      {...props}
      title={<TagTypeEditTitle />}
      transform={transform}
      mutationOptions={{
        onSuccess: handleSuccess,
        onError: handleError
      }}
      redirect={false}
      mutationMode='pessimistic'
    >
      <TagTypeEditForm onDelete={handleDeleteClick} />
      
      <TagTypeDeleteDialog 
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      />
    </Edit>
  );
};

const TagTypeEditForm: React.FC<{ onDelete?: () => void }> = ({ onDelete }) => {
  const record = useRecordContext<TagType>();
  const translate = useTranslate();
  const redirect = useRedirect();
  const { validateName, validateDescription } = useTagTypeValidation();
  
  if (!record) return <LoadingIndicator />;
  
  const handleCancel = () => redirect('list', 'tag-types');

  return (
    <MyFormContainer>
      <SimpleForm
        toolbar={
          <MyToolbar 
            onCancel={handleCancel}
            resource="tag-types"
            showDelete={true}
            onDelete={onDelete}
          />
        }
      >
        <CenteredFormLayout>
          <MyFormSection 
            variant='outlined'
            title={translate('resources.tag-types.sections.details', 'Detalles del Tipo de ID')}
            icon={<TagIcon />}
          >
                       
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <SelectInput
                  source="typeCode"
                  label={translate('resources.tag-types.fields.typeCode', 'Type Code')}
                  choices={VALID_TYPE_CODES}
                  validate={[required()]}
                  fullWidth
                  helperText="Tipo de identificador"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextInput 
                  source="name" 
                  label={translate('resources.tag-types.fields.name', 'Display Name')}
                  validate={[required(), validateName]} 
                  fullWidth 
                  helperText="Nombre legible"
                  size="small"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextInput 
                  source="description" 
                  label={translate('resources.tag-types.fields.description', 'Descripción')}
                  multiline 
                  rows={2} 
                  validate={validateDescription} 
                  fullWidth 
                  helperText="Descripción del tipo de ID"
                  size="small"
                />
              </Grid>
              
              {/* Configuración de comportamiento */}
              <Grid item xs={12}>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Configuración de Comportamiento
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <BooleanInput 
                      source="isSystemGenerated" 
                      label="Generado por Sistema"
                      helperText="Permite que el sistema genere códigos automáticamente"
                    />
                    
                    <BooleanInput 
                      source="requiresManualInput" 
                      label="Requiere Entrada Manual"
                      helperText="El usuario debe introducir manualmente el código"
                    />
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </MyFormSection>
        </CenteredFormLayout>
      </SimpleForm>
    </MyFormContainer>
  );
};

const TagTypeDeleteDialog: React.FC<{
  open: boolean;
  onClose: () => void;
}> = ({ open, onClose }) => {
  const record = useRecordContext<TagType>();
  
  if (!record) return null;
  
  return (
    <BaseDeleteDialog
      open={open}
      onClose={onClose}
      recordId={record.id}
      resource="tag-types"
      warningMessage={
        `¿Estás seguro de que deseas eliminar el tipo de ID "${record.name}"? 
        Esta acción no se puede deshacer y podría afectar a los IDs existentes.`
      }
    />
  );
};

export default TagTypeEdit;